import { ServiceConfigurationResponse } from '@/pages/Settings/constants'
import { isUUID } from '@/utils'
import { $settle } from '@/utils/promise'
import { ServiceEventHandlers, ServiceProps } from '@Protium/vue-ozone-utils/types/models'
import axios, { AxiosError, AxiosInstance, AxiosProgressEvent } from 'axios'
import CryptoJS from 'crypto-js'
import allSettled from 'promise.allsettled'

interface LoginOptions {
  url?: string,
  method?: string,
  tenant?: string
}

export enum ContentType {
  User = 'user'
}

interface ServiceConfigurationEndpoints {
  get (services: Array<string>): any,
  update (service: string, data: any): any,
  delete (service: string, data: any): any
}

const timerangeQuery = (timerange, resolution) => [{ field: 'start', match: timerange.start }, { field: 'end', match: timerange.end }, { field: 'resolution', match: resolution }]

export default class Sys {
  public request: AxiosInstance
  private config
  private handlers: ServiceEventHandlers
  private _baseURL?: string
  constructor(config?: any, props?: ServiceProps) {
    this.config = config

    const baseUrl = ((config || {}).requestConfig || {}).baseURL
    this._baseURL = baseUrl ? baseUrl : process.env.NODE_ENV === 'development' && window.location.host.includes('192.')
      ? `http://${window.location.host.split(':')[0]}:8080`
      : process.env.OZONE_AUTH_PROXY_URL

    this.request = axios.create({
      ...((config || {}).requestConfig || {}),
      // NOTE always uses "auth proxy" on dev, so we can set it to target the local dev server directly
      // otherwise in production, fallback to to proxy only when host baseURL is not yet known/passed
      baseURL: this._baseURL,
      withCredentials: true,
      headers: {
        ...(((config || {}).requestConfig || {}).headers || {}),
        'x-ozone-app-type': process.env.OZONE_SERVICE_TYPE_ID,
        Authorization: `Bearer ${process.env.API_AUTH_TOKEN}`
      }
    })

    // NOTE explicity bind following funtions to the class context
    // also exposes them via the "with" method
    this.get = this.get.bind(this)
    this.post = this.post.bind(this)
    this.put = this.put.bind(this)
    this.patch = this.patch.bind(this)
    this.delete = this.delete.bind(this)

    this.handlers = props?.handlers || { Error: e => Promise.reject(e), Warn: e => Promise.reject(e), Fatal: e => Promise.reject(e), Unauthorized: e => Promise.reject(e) }
  }

  get baseURL () {
    return this._baseURL
  }

  // NOTE presets the path for concurrent method calls
  with (endpoint = '') {
    return Object.fromEntries(
      Object.keys(this).map(key => ([
        key,
        (path, ...args) => {
          // COMBAK bootleg AF (version of the ozone-utils request query parser)
          const query = isUUID(path) || args.length === 2 ? (Array.isArray(path) ? path : [path]) : args[0]
          const data = isUUID(path) || args.length === 2 ? (args[0] || args[1]) : typeof path === 'object' && !args.length ? path : args[1]
          const config = args[2]
          return this[key](`${endpoint}${isUUID(path) || (!args.length || args.length === 2) ? '' : path}`, query, data, config).catch(e => this.handlers.Error(e))
        }
      ]))
    )
  }

  parseQuery (query) {
    return isUUID(query) ? [query] : query
  }

  get (url: string, query?, config?) {
    return this.request({
      url,
      method: 'get',
      params: this.parseQuery(query),
      headers: config ? { 'x-ozone-service-config': JSON.stringify(config) } : {}
    }).then(res => res.data).catch(e => this.handlers.Error(e))
  }

  post (url: string, query, data, config?) {
    return this.request({
      url,
      method: 'post',
      params: this.parseQuery(query),
      data,
      headers: config ? { 'x-ozone-service-config': JSON.stringify(config) } : {}
    }).then(res => res.data).catch(e => this.handlers.Error(e))
  }

  put (url: string, query, data, config) {
    return this.request({
      url,
      method: 'put',
      params: this.parseQuery(query),
      data,
      headers: config ? { 'x-ozone-service-config': JSON.stringify(config) } : {}
    }).then(res => res.data).catch(e => this.handlers.Error(e))
  }

  patch (url: string, query, data, config?) {
    return this.request({
      url,
      method: 'patch',
      params: this.parseQuery(query),
      data,
      headers: config ? { 'x-ozone-service-config': JSON.stringify(config) } : {}
    }).then(res => res.data).catch(e => this.handlers.Error(e))
  }

  delete (url: string, query, config) {
    return this.request({
      url,
      method: 'delete',
      params: this.parseQuery(query),
      headers: config ? { 'x-ozone-service-config': JSON.stringify(config) } : {}
    }).then(res => res.data).catch(e => this.handlers.Error(e))
  }

  login (loginId: string, password: string, options: LoginOptions = { method: 'password' }) {
    const url = options.url || '/auth/login'
    const tenant = options.tenant
    return this.request({
      url,
      method: 'post',
      params: { method: 'password' },
      data: {
        loginId,
        password: CryptoJS.AES.encrypt(password, (process.env.VUE_CRYPTO_SECRET || '')).toString()
      },
      headers: tenant ? { 'x-ozone-tenant-id': tenant } : {}
    }).then(({ data, headers }) => ({ data, headers })).catch(async e => {
      let error = e instanceof AxiosError ? e.response?.data : (e.message || e)

      if (error.statusCode === 404) error = `User ${loginId} not found`
      console.error('LOGIN ERROR', error)
      // pre-parse error
      throw await this.handlers.Error(error)
    })
  }

  userInfo (userId) {
    return this.request({
      url: `/auth/info/${userId}`,
      method: 'get'
    }).then(res => res.data).catch(e => this.handlers.Error(e))
  }

  logout (userId) {
    return this.request({
      url: '/auth/logout',
      method: 'delete',
      params: { user: userId }
    }).catch(e => this.handlers.Error(e))
  }

  register (loginId: string, password: string, userData: any, options: LoginOptions): Promise<any> {
    const url = options.url || '/auth/register'
    const tenant = options.tenant

    return this.request({
      url,
      method: 'post',
      data: {
        email: loginId,
        password: CryptoJS.AES.encrypt(password, (process.env.VUE_CRYPTO_SECRET || '')).toString(),
        ...userData
      },
      headers: tenant ? { 'x-ozone-tenant-id': tenant } : {}
    }).then(({ data, headers }) => ({ data, headers }))
  }

  // multipartform data stream
  stream (url: string, query, input: string | Record<string, any>, config?, onUploadProgress?: (e: AxiosProgressEvent) => void) {
    if (!input) throw Error('Missing Input')
    const headers: Record<string, any> = config ? { 'x-ozone-service-config': JSON.stringify(config) } : {}

    const data = new FormData()

    // add the data as a Blob/file
    switch (typeof input) {
      case 'object':
        data.append('raw', new Blob([JSON.stringify(input)], { type: 'application/json' }))
        break
      case 'string':
      case 'number':
      case 'boolean':
        data.append('raw', new Blob([input], { type: 'string' }))
        break
    }

    return this.request({
      url,
      method: 'post',
      params: this.parseQuery(query),
      data,
      headers,
      onUploadProgress
    }).then(res => res.data).catch(e => this.handlers.Error(e))
  }

  get services () {
    return {
      get: (service) => {
        return this.request({
          url: `/service/${service}`,
          method: 'get'
        }).catch(e => this.handlers.Error(e))
      },
      delete: (serviceId) => {
        return this.request({
          url: '/service/ctrl/service',
          method: 'delete',
          params: { query: serviceId }
        }).catch(e => this.handlers.Error(e))
      },
      create: (type, id, { name, active, config }) => {
        // console.log('service.create', JSON.stringify({ type, id, name, active, config }))
        return this.request({
          url: '/service/ctrl/service',
          method: 'put',
          data: { data: { type, id, name, active, config, creator: 'directr' } }
        })
          .then(res => res.data)
          .catch(e => this.handlers.Error(e))
      },
      patch: (type, id, data: { name?: string, active?: boolean, config?: any }) => {
        const { name, active, config } = (data || {})
        // console.log('service.patch', JSON.stringify({ type, id, name, active, config }))
        return this.request({
          url: '/service/ctrl/service',
          method: 'patch',
          params: { query: id },
          data: { data: { type, id, name, active, config } }
        })
          .then(res => res.data)
          .catch(e => this.handlers.Error(e))
      },
    }
  }

  get configure () {
    return {
      get: (services: Array<string> = []): Promise<Array<ServiceConfigurationResponse>> => {
        return $settle(services.map(service => this.request({
          url: `/service/${service}/config`,
          method: 'get'
        }))).then(({ fulfilled, rejected }) => {
          if (rejected.length) console.warn('[Sys] configure.get | failed to get some configurations', rejected)
          return fulfilled.map(f => f.data)
        }).catch(e => this.handlers.Error(e))
      },
      update: (service: string, data) => {
        return this.request({
          url: `/service/${service}/config`,
          method: 'patch',
          data
        }).catch(e => this.handlers.Error(e))
      },
      delete: (service: string, data) => {
        return this.request({
          url: `/service/${service}/config?[]=${data}`,
          method: 'delete',
          data
        }).catch(e => this.handlers.Error(e))
      }
    }
  }

  get settings () {
    return {
      get: () => {
        return this.request({
          url: '/service/sys/tenant',
          method: 'get',
          headers: { 'X-Ozone-request-config': JSON.stringify({ limit: 1, role: 'admin', order: 'config_version', includeSys: false }) },
          params: [{ select: ['config_version', 'id', 'version_id', 'api_version', 'settings'] }]
        })
      },
      set: (id, settings) => {
        const data = settings
        return this.request({
          url: '/service/sys/tenant',
          method: 'patch',
          headers: { 'X-Ozone-request-config': JSON.stringify({ role: 'admin' }) },
          params: { query: [{ field: '__id', method: 'is', match: id }] },
          data: { data }
        })
      }
    }
  }

  get connections () {
    return {
      list: () => {
        // ??? list current connections? or should this just be visible in the services overview?
        // return this.request({
        //   url: '/auth/connection_requests',
        //   method: 'get'
        // })
      },
      pending: () => {
        return this.request({
          url: '/auth/connection_requests',
          method: 'get'
        })
      },
      accept: (id: string) => {
        return this.request({
          url: `/auth/connection_requests/accept/${id}`,
          method: 'patch'
        })
      },
      reject: (id: string) => {
        return this.request({
          url: `/auth/connection_requests/reject/${id}`,
          method: 'patch'
        })
      }
    }
  }

  get insight () {
    return {
      get: async (service: string, timerange: { start: Date | string, end: Date | string }, resolution = 'day') => {
        return this.get(`service/${service}/insight`, timerangeQuery(timerange, resolution))
      }
    }
  }
}

interface SysRequestHandlerInstance {
  login (loginId: string, password: string, options: LoginOptions): Promise<any>
  logout (token: string, appId: string): Promise<any>
  configure: ServiceConfigurationEndpoints
}
